<template>
  <div class="segment-dashboard" ref="segmentDashboardRef">
    <div class="all-elements">
      <div class="dimension-elements">
        <div class="searcher-input">
          <input v-model="dimensionSearcher" @input="doDimensionsSearch()" />
          <transition name="fade" mode="out-in">
            <i v-if="dimensionSearcher.trim() === ''" class="sherpa-icon icon-buscar"></i>
            <i v-else class="sherpa-icon icon-cerrar" @click="dimensionSearcherReset()"></i>
          </transition>
          <transition name="fade" mode="out-in">
            <div class="searcher-count-results">{{ dimensionSearcherCount }}/{{ dimensionSearcherTotal }}</div>
          </transition>
        </div>
        <simplebar data-simplebar-auto-hide="false">
          <draggable
            class="list-group"
            v-bind="dragDimensionOptions"
            v-model="showList"
            item-key="id"
            @start="dragAllElementStart"
            @end="dragAllElementEnd"
            @change="onDragChange"
            :disabled="dimensionSearcher.trim() !== ''"
          >
            <template #item="{ element }">
              <div
                :key="element.id"
                :class="['list-group-item', {'reorder': dimensionSearcher.trim() === ''}, { selected: isSelected(element) }]"
                @click="toggleSelect(element)"
              >
                <div v-if="containerRef" class="dimension-label" :title="dimensionLabel(element)">
                  {{ dimensionLabel(element) }}
                </div>
              </div>
            </template>
          </draggable>
        </simplebar>
      </div>
    </div>
    <div class="chart">
      <spinner-loading v-if="dashboardDataLoading" class="spinner-loading" :line-size='2' line-fg-color='#5dbecd' />
      <div v-show="!dashboardDataLoading" class="chart-container" ref="chartContainer"></div>
    </div>
  </div>
</template>

<script>
import * as appTypes from '@/store/actions/app'
import draggable from 'vuedraggable'
import simplebar from 'simplebar-vue'
import languages from '@/language/languages'
import debounce from 'lodash/debounce'
import Configuration from '@/configuration'
import * as api from '../definitionLoader/utils/definitionApi'
import moment from 'moment'
import embed from 'vega-embed'

export default {
  name: 'segment-dashboard',
  components: {
    draggable,
    simplebar
  },
  props: [
    'content',
    'type',
    'apiDefinitionId'
  ],
  data () {
    return {
      dragAllElement: false,
      showList: [],
      dimensionsList: null,
      selectedItems: [],
      dimensionSearcher: '',
      dimensionSearcherCount: 0,
      dimensionSearcherTotal: 0,
      refsReady: false,
      dashboardEndpoint: null,
      dashboardDataLoading: false,
      dashboardChartData: [],
      chartView: null,
      resizeObserver: null,
      currentDimensionField: null
    }
  },
  beforeMount () {
    this.dashboardLoaded()
    this.doDimensionsSearch()
  },
  mounted () {
    this.$nextTick(() => {
      this.refsReady = true
      this.initResizeObserver()
      this.toggleSelect(this.dimensionsList[0])
    })
  },
  beforeUnmount () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect() // <--- Añadido
    }
  },
  methods: {
    dashboardLoaded () {
      if (this.content) {
        this.$emit('refreshDefinitionTitle', this.content.segmentName)
        this.dimensionsList = JSON.parse(JSON.stringify(this.content.dimensionsList))
        this.dashboardEndpoint = this.content.dashboardEndpoint
        this.$emit('definitionLoaded', this.content)
      }
      this.$store.dispatch(`${appTypes.APP_NAMESPACE}/${appTypes.APP_LOADING}`, false)
    },
    dragAllElementStart () {
      this.dragAllElement = true
    },
    dragAllElementEnd () {
      this.dragAllElement = false
    },
    getLang (key) {
      return languages[this.$ml.current].segmentBuilder[key]
    },
    doDimensionsSearch () {
      this.dimensionSearcherCount = 0
      this.dimensionSearcherTotal = 0
      this.showList = []
      for (var i = 0; i < this.dimensionsList.length; i++) {
        var dimension = this.dimensionsList[i]

        this.dimensionSearcherTotal += 1
        if (!this.dimensionSearcher || this.dimensionSearcher.trim() === '') {
          this.dimensionSearcherCount += 1
          this.showList.push(dimension)
        } else {
          if (
            this.normalizeText(this.dimensionLabel(dimension)).indexOf(this.normalizeText(this.dimensionSearcher.trim())) > -1
          ) {
            this.dimensionSearcherCount += 1
            this.showList.push(dimension)
          }
        }
      }
    },
    dimensionLabel (dim) {
      if (!dim.indexField) return dim.title
      try {
        const fields = JSON.parse(dim.indexField)
        const fieldStr = (Array.isArray(fields) ? fields : [dim.indexField]).join(' ')
        return `${dim.title} (${fieldStr})`
      } catch {
        return dim.title
      }
    },
    normalizeText (text) {
      return text
        .toLowerCase() // Convertir a minúsculas
        .normalize('NFD') // Descomponer caracteres acentuados
        .replace(/[\u0300-\u036f]/g, '') // Eliminar diacríticos
    },
    dimensionSearcherReset () {
      this.dimensionSearcher = ''
      this.doDimensionsSearch()
    },
    onDragChange (event) {
      // Solo actualizamos el orden en dimensionsList si NO hay búsqueda activa
      if (this.dimensionSearcher.trim() === '') {
        // Creamos una nueva lista con el orden actualizado
        const newOrderIds = this.showList.map(item => item.id)

        // Reordenamos dimensionsList según showList
        this.dimensionsList = newOrderIds.map(id =>
          this.dimensionsList.find(dim => dim.id === id)
        )
      }
    },
    toggleSelect (item) {
      const index = this.selectedItems.indexOf(item.id)
      if (index === -1) {
        this.selectedItems = []
        this.selectedItems.push(item.id)
      } else {
        this.selectedItems.splice(index, 1)
      }
    },
    isSelected (item) {
      return this.selectedItems.includes(item.id)
    },
    getDashboardData (list) {
      if (this.chartView) {
        this.chartView.finalize()
        this.chartView = null
        this.dashboardChartData = null
        this.currentDimensionField = null

          // 2) Espera a que Vue actualice el DOM y entonces borra manualmente todo hijo
        this.$nextTick(() => {
          const container = this.$refs.chartContainer
          if (container) {
            while (container.firstChild) {
              container.removeChild(container.firstChild)
            }
          }
        })
      }
      if (list && list.length > 0) {
        this.dashboardDataLoading = true
        api.call({domainEndpoint: Configuration.value('API_SERVICES_URL') + moment().valueOf(), url: this.dashboardEndpoint, token: null, version: null, method: 'POST', lang: this.$ml.current.toLowerCase(), authorization: window.localStorage.getItem('user-token'), data: { id: this.apiDefinitionId, code: null, jsonData: list }})
          .then(resp => {
            this.dashboardChartData = resp.data
            this.currentDimensionField = resp.currentDimensionField
            this.dashboardDataLoading = false
            this.$nextTick(() => {
              this.renderDashboardChart()
            })
          })
          .catch(err => {
            console.log(err)
            this.dashboardDataLoading = false
          })
      }
    },
    initResizeObserver () {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }

      this.resizeObserver = new ResizeObserver(() => {
        if (this.dashboardChartData && this.dashboardChartData.length > 0) {
          this.renderDashboardChart()
        }
      })

      if (this.$refs.chartContainer) {
        this.resizeObserver.observe(this.$refs.chartContainer)
      }
    },
    renderDashboardChart () {
      const container = this.$refs.chartContainer
      if (!container) return

      // El nombre del campo X que quieras pintar, p. ej. 'professionLanguage'
      // Podrías sacarlo de this.content.dimensionField, o pasarlo como prop al componente
      const xField = this.currentDimensionField || 'categoryLanguage'

      // Limpia y haz visible el contenedor
      if (this.chartView) {
        this.chartView.finalize()
        container.innerHTML = ''
        this.chartView = null
      }
      const width = container.clientWidth - 40
      const height = 500

      const spec = {
        $schema: 'https://vega.github.io/schema/vega/v4.3.json',
        width: width,
        height: height,

        autosize: {
          type: 'fit',
          contains: 'padding'
        },

        data: [
          {
            name: 'table',
            values: this.dashboardChartData
          }
        ],

        signals: [
          {
            name: 'xField',
            value: xField
          }
        ],

        scales: [
          {
            name: 'xscale',
            type: 'band',
            domain: {
              data: 'table',
              field: { signal: 'xField' }
            },
            range: 'width',
            padding: 0.05
          },
          {
            name: 'yscale',
            domain: {
              data: 'table',
              field: 'count'
            },
            nice: true,
            range: 'height'
          }
        ],

        axes: [
          {
            orient: 'bottom',
            scale: 'xscale',
            labelFont: 'Source Sans Pro',
            labelFontSize: 13,
            labelPadding: 10,
            labelAngle: -15,
            labelLimit: 140
          },
          {
            orient: 'left',
            scale: 'yscale',
            labelFont: 'Source Sans Pro',
            labelFontSize: 14,
            format: ',.0f',
            formatType: 'number'
          }
        ],

        marks: [
          {
            type: 'rect',
            from: { data: 'table' },
            encode: {
              enter: {
                x: { scale: 'xscale', field: { signal: 'xField' } },
                width: { scale: 'xscale', band: 1 },
                y: { scale: 'yscale', field: 'count' },
                y2: { scale: 'yscale', value: 0 }
              },
              update: {
                fill: { value: '#076cfabe' },
                cursor: { value: 'default' }
              },
              hover: {
                fill: { value: '#4ab800c5' },
                cursor: { value: 'pointer' }
              }
            }
          },

          {
            type: 'text',
            from: { data: 'table' },
            encode: {
              enter: {
                x: {
                  scale: 'xscale',
                  field: { signal: 'xField' },
                  offset: { signal: "bandwidth('xscale') / 2" }
                },
                y: {
                  scale: 'yscale',
                  field: 'count',
                  offset: -5
                },
                text: {
                  signal: "format(datum.count, ',.0f')"
                },
                align: { value: 'center' },
                baseline: { value: 'bottom' },
                fill: { value: 'black' },
                font: { value: 'Source Sans Pro' },
                fontSize: { value: 13 }
              }
            }
          }
        ],

        config: {
          locale: {
            number: {
              decimal: ',',
              thousands: '.',
              grouping: [3]
            }
          }
        }
      }

      embed(container, spec, {
        actions: false,
        renderer: 'canvas'
      }).then(result => {
        this.chartView = result.view
      }).catch(console.error)
    }
  },
  computed: {
    dragDimensionOptions () {
      return {
        animation: 200,
        group: {
          name: 'dimension',
          put: true,
          pull: true
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: true
      }
    },
    containerRef () {
      return this.refsReady ? this.$refs['segmentDashboardRef'] : null
    },
    selectedItemsFingerprint () {
      if (!this.dimensionsList || !Array.isArray(this.dimensionsList)) return '[]'
      const orderedSelectedIds = this.dimensionsList
        .filter(dim => this.selectedItems.includes(dim.id))
        .map(dim => dim.id)
      return JSON.stringify(orderedSelectedIds)
    }
  },
  watch: {
    selectedItemsFingerprint: {
      handler: debounce(function () {
        const orderedSelected = this.dimensionsList
          .filter(dim => this.selectedItems.includes(dim.id))
          .map(dim => dim.id)

        this.getDashboardData(orderedSelected)
      }, 600),
      immediate: false
    }
  }
}
</script>

<style lang="scss">
.segment-dashboard {
  position: fixed;
  left: 111px;
  right: 12px;
  bottom: 70px;
  top: 133px;
  display: flex;
  gap: 10px;
  background-color: #fafafa;

  /* Animaciones para fade */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-enter-active {
    transform: translateY(-15px);
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-20px);
  }
  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  /* Animaciones para fade-slide-left */
  .fade-slide-left-enter-active,
  .fade-slide-left-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-slide-left-enter-active {
    transform: translateX(-20px);
  }
  .fade-slide-left-enter-from,
  .fade-slide-left-leave-to {
    opacity: 0;
    transform: translateX(-25px);
  }
  .fade-slide-left-enter-to,
  .fade-slide-left-leave-from {
    opacity: 1;
    transform: translateX(0);
  }

  /* Animaciones para fade-slide-right */
  .fade-slide-right-enter-active,
  .fade-slide-right-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-slide-right-enter-active {
    transform: translateX(20px);
  }
  .fade-slide-right-enter-from,
  .fade-slide-right-leave-to {
    opacity: 0;
    transform: translateX(25px);
  }
  .fade-slide-right-enter-to,
  .fade-slide-right-leave-from {
    opacity: 1;
    transform: translateX(0);
  }

  .all-elements {
    flex-basis: 23%;
    max-width: 23%;
    min-width: 23%;
    display: flex;
    flex-direction: column;
    background-color: #f5f5f5;
    border: 1px dotted #999;
    overflow: hidden;
    position: relative;
    z-index: 0;

    .dimension-elements {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;

      [data-simplebar] {
        height: 93%;
        margin-top: 8px;
      }

      .dimension-item {
        display: flex;
        gap: 2px;
      }
    }

    .list-group {
      display: flex;
      flex-direction: column;
      gap: 10px;
      min-height: 80px;
      margin: 0;
      padding: 0 10px;

      &.fix {
        min-height: initial;
      }
    }

    .list-group-item {
      cursor: pointer;
      padding: 0;
      background-color: #fff6b4;
      overflow: hidden;

      .dimension-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      border: 1px solid #53e8ff;
      border-radius: 4px;
      margin-bottom: 5px;
      font-family: 'Source Sans Pro';
      font-size: 15px;
      font-weight: 400;
      transition: all 0.2s ease;
      position: relative;
      padding: 7px 8px 6px;
      opacity: 0.5;

      &.reorder {
        cursor: move;
      }

      &:hover {
        border: 1px solid #999;
        opacity: 0.65;
      }

      &.selected {
        border: 1px solid #999;
        opacity: 1;
      }

      i {
        font-size: 18px;
        margin-right: 0;
        color: #7f7f7f;
        width: 18px;
        vertical-align: top;

        &.item-icon {
          position: absolute;
        }
      }
    }

    .ghost {
      opacity: 0.8;
      background-color: #c8ebfb;
    }

    .searcher-input {
      padding: 10px 10px 0;

      input {
        border: 0;
        outline: none;
        font-family: 'Source Sans Pro';
        padding: 5px 65px 5px 10px;
        border: 1px solid #ddd;
        width: 100%;
        transition: border 0.2s ease;

        &:hover,
        &:focus {
          border: 1px solid #999;
        }
      }

      i {
        position: absolute;
        right: 20px;
        top: 18px;
        color: #d42e12;

        &.icon-cerrar {
          cursor: pointer;
        }
      }

      .searcher-count-results {
        position: absolute;
        right: 41px;
        top: 21px;
        font-size: 10px;
        font-style: italic;
        color: #9f9f9f;
      }
    }
  }

  .chart {
    width: 76%;
    max-width: 76%;
    min-width: 76%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: #e4fffe;
    border: 1px dotted #999;
    padding: 20px;

    .chart-container  {
      width: 100%;
    }

    > span {
      position: relative;
    }

    .spinner-loading {
      position: absolute;
      right: 0;
      left: 20px;
      top: 19px;
    }
  }
}
</style>
